import { IRenditionReport } from 'm3u8-parser';

import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { Logger, TLogger } from '@logger/Logger';
import { BasePlaylistRotationController } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController';
import { FragmentLoader } from '@streamLoader/FragmentLoader/FragmentLoader';
import { LLNewPlaylistRotationController } from '@streamLoader/LLPlaylistRotationController/LLNewPlaylistRotationController';
import { SimplePlaylistRotationController } from '@streamLoader/SimplePlaylistRotationController/SimplePlaylistRotationController';
import { StreamLoaderModuleEvents } from '@streamLoader/StreamLoader.events';
import { VODPlaylistRotationController } from '@streamLoader/VODPlaylistRotationController/VODPlaylistRotationController';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';

import { NetworkLoaderEvent, TNetworkLoaderEvents } from './net/NetworkLoader.events';

export enum EPlaylistRotationControllerType {
  VOD = 'VOD',
  LOW_LATENCY = 'lowLatency',
  SIMPLE = 'simple',
}
export class StreamLoader implements IDebbugable {
  public _eventBus: TEventBus;
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['loader'];
  private _streamUrl: string | null = null;
  private _aclToken: string | null = null;
  private _playlistsRotator: BasePlaylistRotationController | null = null;
  private _fragmentsLoader: FragmentLoader;
  private _actualRenditionReport: IRenditionReport | null = null;
  private _lastAddedFragment: Fragment | null = null;
  private readonly _logger: TLogger;
  private readonly _simplePlaylistDetectedListener: string;
  private readonly _llPlaylistDetectedListener: string;
  private readonly _vodPlaylistDetectedListener: string;
  private readonly _onSoftResetRequestedListener: string;
  private readonly _onFragmentAddedListener: string;
  private readonly _onBrowserRedirectDetectedListener: string;
  private readonly _initialType: EPlaylistRotationControllerType;

  constructor(
    initialType: EPlaylistRotationControllerType = EPlaylistRotationControllerType.SIMPLE,
    lastFragment: Fragment | null,
    eventBus: TEventBus,
  ) {
    this._eventBus = eventBus;
    this._logger = Logger(this._eventBus, 'Doppio - StreamLoader');

    this._logger.info('Created');

    this._fragmentsLoader = new FragmentLoader(this._eventBus);
    this._initialType = initialType;

    this._simplePlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.simpleDetected,
      this._onSimplePlaylistDetected,
    );

    this._llPlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.llDetected,
      this._onLLPlaylistDetected,
    );

    this._vodPlaylistDetectedListener = this._eventBus.subscribe(
      StreamLoaderModuleEvents.playlistRotator.playlistType.vodDetected,
      this._onVODPlaylistDetected,
    );

    this._onSoftResetRequestedListener = this._eventBus.subscribe(
      BufferControllerEvents.requestResetWithOverplay,
      this._resetPlaylistLoaders,
    );

    this._onBrowserRedirectDetectedListener = this._eventBus.subscribe(
      NetworkLoaderEvent.events.browserRedirectDetected,
      this._onBrowserRedirectDetected,
    );

    this._onFragmentAddedListener = this._eventBus.subscribe(BufferControllerEvents.fragmentAdded, this._onFragmentAdded);

    this._lastAddedFragment = lastFragment;
  }

  public startLoad(streamUrl: string): void {
    this._streamUrl = streamUrl;

    this._recreateLoaders();
  }

  public destroy(): void {
    this._logger.info(`Destroying instance, ${this._streamUrl}`);

    this._fragmentsLoader.destroy();
    this._playlistsRotator?.destroy();

    this._eventBus.unsubscribe(this._simplePlaylistDetectedListener);
    this._eventBus.unsubscribe(this._llPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._vodPlaylistDetectedListener);
    this._eventBus.unsubscribe(this._onSoftResetRequestedListener);
    this._eventBus.unsubscribe(this._onFragmentAddedListener);
    this._eventBus.unsubscribe(this._onBrowserRedirectDetectedListener);
  }

  public setActualRenditionReport(report: IRenditionReport | null): void {
    if (report) {
      this._actualRenditionReport = report;
    }
  }

  private _recreateLoaders(): void {
    this.status = 'Restarting';
    const url: string | null = this._buildUrl();

    this._logger.debug('Recreate loaders');

    if (!url) {
      return;
    }

    this._fragmentsLoader.destroy();
    this._fragmentsLoader = new FragmentLoader(this._eventBus);

    if (!this._playlistsRotator) {
      this._recreateInitialLoader();
    } else {
      this._playlistsRotator?.setURL(url);
    }

    this.status = 'Working';
  }

  private _recreateInitialLoader(): void {
    switch (this._initialType) {
      case EPlaylistRotationControllerType.SIMPLE:
        this._onSimplePlaylistDetected();
        break;

      case EPlaylistRotationControllerType.VOD:
        this._onVODPlaylistDetected();
        break;

      case EPlaylistRotationControllerType.LOW_LATENCY:
        this._onLLPlaylistDetected();
        break;

      default:
        this._onSimplePlaylistDetected();
    }
  }

  private _buildUrl(): string | null {
    if (!this._streamUrl) {
      return null;
    }

    return `${this._streamUrl}${this._aclToken ? '?aclToken=' + this._aclToken : ''}`;
  }

  @bind
  private _onFragmentAdded(fragment: Fragment): void {
    this._lastAddedFragment = fragment;
  }

  @bind
  private _onSimplePlaylistDetected(manifest?: IHydratedManifest): void {
    if (this._playlistsRotator instanceof SimplePlaylistRotationController) {
      return;
    }

    this._eventBus.publish(StreamLoaderModuleEvents.streamLoader.playlistRotatorTypeChanged, EPlaylistRotationControllerType.SIMPLE);

    this._logger.info('Simple playlist detected, switching to simple loader');

    const url: string | null = this._buildUrl();

    if (!url) {
      return;
    }

    this._playlistsRotator?.destroy();
    this._playlistsRotator = new SimplePlaylistRotationController(this._eventBus, url, manifest);

    this.additionalInfo.playlistRotatorType = 'SimplePlaylistRotator';
  }

  @bind
  private _onVODPlaylistDetected(manifest?: IHydratedManifest): void {
    if (this._playlistsRotator instanceof VODPlaylistRotationController) {
      return;
    }

    this._eventBus.publish(StreamLoaderModuleEvents.streamLoader.playlistRotatorTypeChanged, EPlaylistRotationControllerType.VOD);

    this._logger.info('VOD playlist detected, switching to VOD loader');

    const url: string | null = this._buildUrl();

    if (!url) {
      return;
    }

    this._playlistsRotator?.destroy();
    this._playlistsRotator = new VODPlaylistRotationController(this._eventBus, url, manifest);

    this.additionalInfo.playlistRotatorType = 'VODPlaylistRotator';
  }

  @bind
  private _onLLPlaylistDetected(manifest?: IHydratedManifest): void {
    if (this._playlistsRotator instanceof LLNewPlaylistRotationController) {
      return;
    }

    this._eventBus.publish(StreamLoaderModuleEvents.streamLoader.playlistRotatorTypeChanged, EPlaylistRotationControllerType.LOW_LATENCY);

    this._logger.info('LL Playlist detected, switching to ll loader');

    const url: string | null = this._buildUrl();

    if (!url) {
      return;
    }

    this._playlistsRotator?.destroy();
    this._playlistsRotator = new LLNewPlaylistRotationController(this._eventBus, {
      url,
      startManifest: manifest,
      initialMSN: this._actualRenditionReport?.lastMsn,
      initialPN: this._actualRenditionReport?.lastPart,
      lastLoadedFragment: this._lastAddedFragment,
    });

    this.additionalInfo.playlistRotatorType = 'LowLatencyPlaylistRotator';
  }

  @bind
  private _resetPlaylistLoaders(): void {
    this._playlistsRotator?.abortCurrentPlaylistLoading();
    this.status = 'Idle';
  }

  @bind
  private _onBrowserRedirectDetected({ responseUrl }: TNetworkLoaderEvents['networkLoader.events.browserRedirectDetected']): void {
    this._streamUrl = responseUrl;
    const url: string | null = this._buildUrl();
    if (url) {
      this._playlistsRotator?.setURL(url, true);
    }
  }

  get additionalModules(): Record<string, IDebbugable> {
    const modules: Record<string, IDebbugable> = {};
    if (this._playlistsRotator) {
      modules.playlistRotator = this._playlistsRotator;
    }

    if (this._fragmentsLoader) {
      modules.fragmentLoader = this._fragmentsLoader;
    }

    return modules;
  }
}
